import { Component } from "@angular/core";
import {
  ViewController,
  NavParams,
  ToastController,
  LoadingController
} from "ionic-angular";
import {
  providerLevels,
  danishRegions,
  danishMunicipalities,
  actionTypes,
  diaprofilTopics,
  supportedDiabetesTypes,
  scores
} from "../../app/models/types";
import { ISupportAction } from "../../app/models/support-action";
import { AppState } from "../../app/models/app.state";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { _i18n_str } from "../../assets/i18n/non-static-ids";
import { SupportActionsService } from "../../app/services/support-actions.service";
import { FormBuilder, Validators, FormGroup } from "@angular/forms";
import { DashboardService } from "../../app/services/dashboard.service";
import { WidgetConfig } from "@zitelab/ionic-fhir-questionnaires/dist/models/widget";
import { OptionObject } from "@zitelab/ionic-fhir-questionnaires";

// https://github.com/ionicthemes/ionic-forms-and-validations/tree/master/ionic-3

@Component({
  selector: "manage-support-action-modal",
  templateUrl: "manage-support-action.modal.html"
})
export class ManageSupportActionModal {
  consultationTopicOptions: OptionObject[];
  modalType: "EDIT" | "CREATE";
  selectableWidgets: WidgetConfig<any>[] = [];
  supportAction: ISupportAction;
  form: FormGroup;
  providerLevels = providerLevels;
  danishRegions = danishRegions;
  danishMunicipalities = danishMunicipalities;
  actionTypes = actionTypes;
  actionTopics = diaprofilTopics;
  supportedDiabetesTypes = supportedDiabetesTypes;
  scores = scores;
  _i18n_str = _i18n_str;
  constructor(
    public viewCtrl: ViewController,
    private navParams: NavParams,
    public support: SupportActionsService,
    public dashboard: DashboardService,
    public toastCtrl: ToastController,
    private fb: FormBuilder,
    public store: Store<AppState>,
    public loadingCtrl: LoadingController,
    public translate: TranslateService
  ) {
    this.selectableWidgets = this.dashboard.widgets.filter(
      widget => widget.widgetType !== "LabelWidget" && widget.prefix !== null
    );
    this.consultationTopicOptions = this.dashboard.getConsultationTopicOptions();
    this.modalType = this.navParams.get("modalType") || "CREATE";
    this.supportAction =
      (this.navParams.get("supportAction") as ISupportAction) ||
      <ISupportAction>{};
    this.form = this.fb.group({
      title: [
        this.supportAction.title,
        Validators.compose([Validators.required])
      ],
      description: [this.supportAction.description],
      providerName: [
        this.supportAction.providerName,
        Validators.compose([Validators.required])
      ],
      providerLevel: [
        this.supportAction.providerLevel,
        Validators.compose([Validators.required])
      ],
      providerRegions: [this.supportAction.providerRegions],
      providerMunicipalities: [this.supportAction.providerMunicipalities],
      url: [
        this.supportAction.url,
        Validators.compose([
          Validators.pattern(
            /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[\-;:&=\+\$,\w]+@)?[A-Za-z0-9\.\-]+|(?:www\.|[\-;:&=\+\$,\w]+@)[A-Za-z0-9\.\-]+)((?:\/[\+~%\/\.\w\-_]*)?\??(?:[\-\+=&;%@\.\w_]*)#?(?:[\.\!\/\\\w]*))?)/
          )
        ])
      ],
      phoneNumber: [
        this.supportAction.phoneNumber,
        Validators.compose([
          Validators.pattern(
            /^((\(?\+45\)?)?)(\s?\d{2}\s?\d{2}\s?\d{2}\s?\d{2})$/
          ),
          Validators.minLength(8)
        ])
      ],
      supportForDiabetesType: [
        this.supportAction.supportForDiabetesType,
        Validators.compose([Validators.required])
      ],
      actionType: [
        this.supportAction.actionType,
        Validators.compose([Validators.required])
      ],
      actionTypeOther: [this.supportAction.actionTypeOther],
      actionTopics: [
        this.supportAction.actionTopics,
        Validators.compose([Validators.required])
      ],
      actionTopicOther: [this.supportAction.actionTopicOther],
      questionIds: [
        this.supportAction.questionIds,
        Validators.compose([Validators.required])
      ],
      consultationTopicIds: [this.supportAction.consultationTopicIds],
      consultationTopicIdsVersion2: [
        this.supportAction.consultationTopicIdsVersion2
      ],
      patientWishIds: [this.supportAction.patientWishIds],
      scoreIds: [this.supportAction.scoreIds]
    });
  }

  dismiss($event) {
    this.viewCtrl.dismiss();
  }

  showOtherActionTopic() {
    const value =
      this.form.value.actionTopics !== null &&
      Array.isArray(this.form.value.actionTopics) &&
      this.form.value.actionTopics.includes("OTHER");
    return value;
  }

  submitSupportAction($event) {
    if (this.modalType === "CREATE") {
      this.createSupportAction($event);
    } else {
      this.patchSupportAction($event);
    }
  }

  selectProItemFilter(term: string, item: WidgetConfig<any>) {
    return `${item.prefix} ${item.label}`.indexOf(term) > -1;
  }

  createSupportAction($event) {
    const supportAction = this.form.value as ISupportAction;
    const loading = this.loadingCtrl.create({
      content: this.translate.instant(
        _i18n_str("GENERIC_UI.CREATING_NEW_SUPPORT_ACTION")
      ),
      dismissOnPageChange: true
    });
    loading.present();
    const extraAction = result => {
      loading.dismissAll();
      this.viewCtrl.dismiss();
      const toast = this.toastCtrl.create({
        message: `Handlemulighed oprettet!`,
        duration: 3000,
        position: "top",
        closeButtonText: "Luk",
        showCloseButton: true
      });
      toast.present();
    };
    this.support.postSupportAction(supportAction, extraAction);
  }

  patchSupportAction($event) {
    const supportAction = {
      ...this.supportAction,
      ...(this.form.value as ISupportAction)
    };
    const loading = this.loadingCtrl.create({
      content: this.translate.instant(
        _i18n_str("GENERIC_UI.MODIFYING_SUPPORT_ACTION")
      ),
      dismissOnPageChange: true
    });
    loading.present();
    const extraAction = result => {
      loading.dismissAll();
      this.viewCtrl.dismiss();
      const toast = this.toastCtrl.create({
        message: `Ændringer til handlemulighed gemt!`,
        duration: 3000,
        position: "top",
        closeButtonText: "Luk",
        showCloseButton: true
      });
      toast.present();
    };
    this.support.patchSupportAction(supportAction, extraAction);
  }
}



// WEBPACK FOOTER //
// ./src/modals/manage-support-action.modal/manage-support-action.modal.ts